/**
 * Webtech HR Engine™ - Feature Gate Component
 * Composant pour bloquer l'accès aux fonctionnalités selon le plan
 */

import { usePlan } from '../contexts/PlanContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Lock, Crown, Sparkles, ArrowRight } from 'lucide-react';

/**
 * FeatureGate - Bloque l'accès à une fonctionnalité si le plan ne le permet pas
 * 
 * Usage:
 * <FeatureGate feature="IA_ENABLED">
 *   <MyAIComponent />
 * </FeatureGate>
 */
export function FeatureGate({ 
  feature, 
  children, 
  fallback = null,
  showUpgradePrompt = true 
}) {
  // Modèle commercial désormais basé sur le nombre d'employés —
  // toutes les fonctionnalités sont accessibles à tous les plans.
  return children;
}

/**
 * ModuleGate - Bloque l'accès à un module entier
 */
export function ModuleGate({ 
  module, // 'performance' | 'skills' | 'aiAnalysis'
  children,
  title,
  description
}) {
  // Modèle commercial désormais basé sur le nombre d'employés —
  // tous les modules sont accessibles à tous les plans.
  return children;
}

/**
 * ProBadge - Badge indicateur pour les fonctionnalités PRO
 */
export function ProBadge({ className = '' }) {
  return (
    <Badge className={`bg-amber-100 text-amber-700 text-xs ${className}`}>
      <Crown className="w-3 h-3 mr-1" />
      PRO
    </Badge>
  );
}

/**
 * AIBadge - Badge indicateur pour les fonctionnalités IA
 */
export function AIBadge({ className = '' }) {
  return (
    <Badge className={`bg-purple-100 text-purple-700 text-xs ${className}`}>
      <Sparkles className="w-3 h-3 mr-1" />
      IA
    </Badge>
  );
}

/**
 * LockedOverlay - Overlay grisé pour indiquer qu'une zone est bloquée
 */
export function LockedOverlay({ 
  children, 
  locked = true,
  message = "Fonctionnalité PRO"
}) {
  const navigate = useNavigate();
  
  if (!locked) {
    return children;
  }
  
  return (
    <div className="relative">
      <div className="opacity-30 pointer-events-none select-none filter blur-[1px]">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-slate-900/10 backdrop-blur-[2px] rounded-lg">
        <div className="text-center p-4 bg-white/90 rounded-lg shadow-lg">
          <Lock className="w-8 h-8 mx-auto text-amber-500 mb-2" />
          <p className="text-sm font-medium text-slate-700 mb-2">{message}</p>
          <Button 
            size="sm" 
            className="bg-webtech-blue hover:bg-webtech-blue-hover"
            onClick={() => navigate('/billing/pricing')}
          >
            <Crown className="w-3 h-3 mr-1" />
            Passer au PRO
          </Button>
        </div>
      </div>
    </div>
  );
}

export default FeatureGate;
